import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { useWithdraw } from '@/hooks/wallet';
import { walletApi } from '@/api/wallet-api';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

interface WithdrawalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableBalance: number;
}

export function WithdrawalDialog({
  open,
  onOpenChange,
  availableBalance,
}: WithdrawalDialogProps) {
  const [amount, setAmount] = useState('');
  const [pixKey, setPixKey] = useState('');
  const [pixKeyType, setPixKeyType] = useState<'CPF' | 'CNPJ' | 'EMAIL' | 'PHONE' | 'EVP'>('CPF');
  const [error, setError] = useState('');

  const { data: savedPixKey } = useQuery({
    queryKey: ['pixKeys'],
    queryFn: () => walletApi.getPixKey(),
    enabled: open,
  });

  useEffect(() => {
    if (savedPixKey && !pixKey) {
      if (savedPixKey.key) setPixKey(savedPixKey.key);
      if (savedPixKey.type) setPixKeyType(savedPixKey.type as any);
    }
  }, [savedPixKey, open]);

  const { mutate: withdraw, isPending } = useWithdraw();

  const handleMaxAmount = () => {
    if (availableBalance > 0) {
      setAmount(availableBalance.toFixed(2).replace('.', ','));
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const parsed = parseFloat(amount.replace(',', '.'));
    if (!parsed || isNaN(parsed) || parsed <= 0) {
      setError('Informe um valor válido maior que R$ 0,00');
      return;
    }
    if (parsed > availableBalance) {
      setError('Saldo insuficiente para realizar este saque');
      return;
    }
    if (!pixKey.trim()) {
      setError('Chave PIX é obrigatória');
      return;
    }

    withdraw(
      { amount: parsed, pixKey: pixKey.trim(), pixKeyType },
      {
        onSuccess: () => {
          onOpenChange(false);
          setAmount('');
        },
        onError: (err: any) => {
          const msg = err?.response?.data?.message || err?.message || 'Erro ao solicitar saque. Tente novamente.';
          setError(msg);
        },
      }
    );
  };

  const maxFormatted = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(availableBalance);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card text-card-foreground border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground text-lg font-bold">Solicitar Saque via PIX</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-semibold text-foreground">
                Valor do Saque
              </label>
              <button
                type="button"
                onClick={handleMaxAmount}
                className="text-xs text-primary font-medium hover:underline cursor-pointer"
              >
                Sacar valor total
              </button>
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-semibold">
                R$
              </span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0,00"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^0-9,]/g, ''))}
                className="w-full pl-10 pr-4 py-2.5 border border-border bg-background text-foreground rounded-xl text-base font-semibold focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1.5 flex items-center justify-between">
              <span>Saldo disponível: <strong className="text-foreground">{maxFormatted}</strong></span>
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-1">
              Tipo de Chave PIX
            </label>
            <select
              value={pixKeyType}
              onChange={(e) => setPixKeyType(e.target.value as any)}
              className="w-full px-3 py-2.5 border border-border bg-background text-foreground rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="CPF">CPF</option>
              <option value="CNPJ">CNPJ</option>
              <option value="EMAIL">E-mail</option>
              <option value="PHONE">Telefone</option>
              <option value="EVP">Chave Aleatória (EVP)</option>
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-semibold text-foreground">
                Chave PIX
              </label>
              {savedPixKey && (
                <span className="text-xs text-emerald-600 flex items-center gap-1 font-medium">
                  <CheckCircle2 size={12} /> Chave salva
                </span>
              )}
            </div>
            <input
              type="text"
              placeholder="Digite sua chave PIX"
              value={pixKey}
              onChange={(e) => setPixKey(e.target.value)}
              className="w-full px-3 py-2.5 border border-border bg-background text-foreground rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/20 p-3 rounded-xl">
              <AlertCircle size={16} className="shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
              className="rounded-xl border-border"
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary" isLoading={isPending} className="rounded-xl">
              Confirmar Saque
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
